import { CompanyDetails } from '@/types/companies';
import { Card, Typography } from '@mui/material';
import { SVLink } from '@/components/core';
import { formatDateTimeFromUTC } from '@/utils/date';
import { useState } from 'react';
import ChangeSavingsInterestRateModal from '@/components/companies/modals/ChangeSavingsInterestRateModal';
import { useUpdateCompanySavingsInterestRate } from '@/hooks/requests/useCompanies';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useSentry } from '@/hooks/useSentry';
import { captureException } from '@sentry/browser';

type CompanyInterestRateInfoProps = {
  companyDetails: CompanyDetails;
};

export default function CompanyInterestRateInfo({
  companyDetails,
}: CompanyInterestRateInfoProps) {
  const { rateSavingInterest, rateSavingInterestUpdatedAt, uuid } =
    companyDetails;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    mutate: updateSavingsInterestRate,
    isPending: isUpdateSavingsInterestRatePending,
  } = useUpdateCompanySavingsInterestRate(uuid);
  const { scrollToTop } = useScrollToTop();
  const { addUserActionBreadcrumb } = useSentry();

  const handleChangeSavingsInterestRate = () => {
    addUserActionBreadcrumb(
      'Open savings interest rate change modal',
      'user.action',
      {
        companyUuid: uuid,
        currentRate: rateSavingInterest,
      },
    );
    setIsModalOpen(true);
  };

  const handleSubmitInterestRate = async (data: {
    rateSavingInterest: number;
  }) => {
    try {
      addUserActionBreadcrumb(
        'Submit savings interest rate change',
        'user.action',
        {
          companyUuid: uuid,
          oldRate: rateSavingInterest,
          newRate: data.rateSavingInterest,
        },
      );

      await updateSavingsInterestRate(data.rateSavingInterest);

      showToast({
        type: 'success',
        data: {
          content: `Savings interest rate has been successfully updated to ${data.rateSavingInterest}%`,
        },
      });

      scrollToTop();
    } catch (error) {
      captureException(error, {
        tags: {
          component: 'CompanyInterestRateInfo',
          action: 'update_savings_interest_rate',
        },
        contexts: {
          company: {
            uuid,
            currentRate: rateSavingInterest,
          },
          form: {
            newRate: data.rateSavingInterest,
          },
        },
      });

      showToast({
        type: 'error',
        data: { content: MESSAGES.genericError },
      });
      throw error;
    }
  };

  return (
    <>
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          Savings Interest Rate
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Savings Interest Rate for new savings accounts:{' '}
          <strong>{rateSavingInterest}%</strong> (Last updated on{' '}
          {formatDateTimeFromUTC(rateSavingInterestUpdatedAt, true)})
        </Typography>
        <SVLink to="" onClick={handleChangeSavingsInterestRate}>
          Change the Savings Interest Rate for Existing Bank Accounts
        </SVLink>
      </Card>

      <ChangeSavingsInterestRateModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        currentRate={parseFloat(rateSavingInterest) || 0}
        onSubmit={handleSubmitInterestRate}
        isLoading={isUpdateSavingsInterestRatePending}
      />
    </>
  );
}
